// src/taskpane/services/textReplacer.ts
import { ExtractedTextItem } from "./types";

/**
 * 将翻译后的文本替换回演示文稿的相应位置
 * @param items 已翻译的文本项列表
 * @returns 替换成功的项数
 */
export async function replaceAllText(
  items: ExtractedTextItem[]
): Promise<{ successCount: number; failCount: number }> {
  let successCount = 0;
  let failCount = 0;

  // 只处理翻译成功的项
  const itemsToReplace = items.filter(
    (item) => item.translated && item.translatedText
  );

  await PowerPoint.run(async (context) => {
    const slides = context.presentation.slides;
    slides.load("items");
    await context.sync();

    // 按幻灯片分组
    const slideMap = new Map<number, ExtractedTextItem[]>();
    for (const item of itemsToReplace) {
      const existing = slideMap.get(item.slideIndex) || [];
      existing.push(item);
      slideMap.set(item.slideIndex, existing);
    }

    for (const [slideIndex, slideItems] of slideMap) {
      const slide = slides.getItemAt(slideIndex);
      const shapes = slide.shapes;
      shapes.load("items/type, items/name");
      await context.sync();

      // 用 shapeIndex（位置索引）精确匹配，避免同名形状覆盖问题
      for (const item of slideItems) {
        try {
          const shape = shapes.getItemAt(item.shapeIndex);

          if (item.shapeType === "table") {
            await replaceTableCell(shape, item);
          } else {
            await replaceTextFrame(shape, item);
          }
          successCount++;
        } catch (error) {
          item.error = `替换失败: ${
            error instanceof Error ? error.message : String(error)
          }`;
          failCount++;
        }
      }

      await context.sync();
    }
  });

  return { successCount, failCount };
}

/** 替换 TextFrame 中的文本 */
async function replaceTextFrame(
  shape: PowerPoint.Shape,
  item: ExtractedTextItem
): Promise<void> {
  const textFrame = shape.getTextFrameOrNullObject();
  textFrame.load("isNullObject, hasText");
  await textFrame.context.sync();

  if (textFrame.isNullObject || !textFrame.hasText) {
    throw new Error("文本框为空或不存在");
  }

  // 直接赋值，格式保留在 TextRange 对象上
  textFrame.textRange.text = item.translatedText;
}

/** 替换表格单元格中的文本 */
async function replaceTableCell(
  shape: PowerPoint.Shape,
  item: ExtractedTextItem
): Promise<void> {
  if (item.tableRowIndex === null || item.tableColIndex === null) {
    throw new Error("表格行列索引缺失");
  }

  const table = shape.getTable();
  const cell = table.getCellOrNullObject(
    item.tableRowIndex,
    item.tableColIndex
  );
  cell.load("isNullObject");
  await cell.context.sync();

  if (cell.isNullObject) {
    throw new Error("表格单元格不存在");
  }

  cell.text = item.translatedText;
}
